import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { FileAttachment, FileEntityType } from '@mayksel/types';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useFiles(entityType: FileEntityType, entityId: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<FileAttachment[]>({
    queryKey: ['files', workspaceId, entityType, entityId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<FileAttachment[]>(
        `/v1/files/${entityType}/${entityId}`,
        { token: token ?? undefined, workspaceId: workspaceId ?? undefined },
      );
    },
    enabled: !!workspaceId && !!entityId,
  });
}

export function useUploadFile(entityType: FileEntityType, entityId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      // Use fetch directly — api lib doesn't support FormData
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const res = await fetch(`${baseUrl}/v1/files/${entityType}/${entityId}`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
        },
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message ?? 'Upload failed');
      }

      return res.json() as Promise<{ data: FileAttachment }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['files', workspaceId, entityType, entityId] });
    },
  });
}

export function useDeleteFile(entityType: FileEntityType, entityId: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const token = await getToken();
      return api.delete(`/v1/files/${fileId}`, { token: token ?? undefined, workspaceId: workspaceId ?? undefined });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['files', workspaceId, entityType, entityId] });
    },
  });
}

/** Returns the URL to stream a file's content through the API. */
export function getFileDownloadUrl(fileId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? '';
  return `${baseUrl}/v1/files/${fileId}/content`;
}
